import { useState, useEffect, useCallback } from 'react';
import { CheckCircle, XCircle, AlertCircle, Info, X } from 'lucide-react';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastMessage {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
}

interface ToastProps {
  toast: ToastMessage;
  onDismiss: (id: string) => void;
}

const icons: Record<ToastType, React.ReactNode> = {
  success: <CheckCircle className="w-5 h-5 text-emerald" />,
  error: <XCircle className="w-5 h-5 text-ruby" />,
  warning: <AlertCircle className="w-5 h-5 text-gold" />,
  info: <Info className="w-5 h-5 text-sapphire" />,
};

const styles: Record<ToastType, string> = {
  success: 'border-emerald/30 bg-emerald/10',
  error: 'border-ruby/30 bg-ruby/10',
  warning: 'border-gold/30 bg-gold/10',
  info: 'border-sapphire/30 bg-sapphire/10',
};

function ToastItem({ toast, onDismiss }: ToastProps) {
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    // Default duration is 5 seconds (extended from typical 3s for better readability)
    const duration = toast.duration ?? 5000;
    const timer = setTimeout(() => {
      setIsExiting(true);
      setTimeout(() => onDismiss(toast.id), 300);
    }, duration);

    return () => clearTimeout(timer);
  }, [toast.id, toast.duration, onDismiss]);

  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 rounded-xl border backdrop-blur-sm shadow-lg ${styles[toast.type]} ${isExiting ? 'toast-exit' : 'toast'}`}
      role="alert"
      aria-live="polite"
    >
      {icons[toast.type]}
      <p className="flex-1 text-sm text-text-primary">{toast.message}</p>
      <button
        onClick={() => {
          setIsExiting(true);
          setTimeout(() => onDismiss(toast.id), 300);
        }}
        className="p-1 rounded-lg hover:bg-white/10 transition-colors touch-target"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4 text-text-muted" />
      </button>
    </div>
  );
}

// Toast state management
let toastId = 0;
let listeners: Array<(toasts: ToastMessage[]) => void> = [];
let toasts: ToastMessage[] = [];

const notifyListeners = () => {
  listeners.forEach(listener => listener([...toasts]));
};

export const toast = {
  success: (message: string, duration?: number) => {
    const id = `toast-${++toastId}`;
    toasts = [...toasts, { id, type: 'success', message, duration }];
    notifyListeners();
  },
  error: (message: string, duration?: number) => {
    const id = `toast-${++toastId}`;
    toasts = [...toasts, { id, type: 'error', message, duration }];
    notifyListeners();
  },
  warning: (message: string, duration?: number) => {
    const id = `toast-${++toastId}`;
    toasts = [...toasts, { id, type: 'warning', message, duration }];
    notifyListeners();
  },
  info: (message: string, duration?: number) => {
    const id = `toast-${++toastId}`;
    toasts = [...toasts, { id, type: 'info', message, duration }];
    notifyListeners();
  },
};

export function ToastContainer() {
  const [currentToasts, setCurrentToasts] = useState<ToastMessage[]>([]);

  useEffect(() => {
    listeners.push(setCurrentToasts);
    return () => {
      listeners = listeners.filter(l => l !== setCurrentToasts);
    };
  }, []);

  const handleDismiss = useCallback((id: string) => {
    toasts = toasts.filter(t => t.id !== id);
    notifyListeners();
  }, []);

  if (currentToasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 left-4 sm:left-auto sm:w-96 z-50 flex flex-col gap-2">
      {currentToasts.map(t => (
        <ToastItem key={t.id} toast={t} onDismiss={handleDismiss} />
      ))}
    </div>
  );
}

export default ToastContainer;
